import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';
import { Link } from 'react-router-dom';

import { TransientProps } from '@common/types';

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.875rem;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: var(--colors-brand-primary);

  > h2 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--colors-foreground-100);
  }
`;

export const ActiveBadge = styled.span`
  ${({ theme }) => css`
    font-size: 0.625rem;
    font-weight: 600;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    padding: 0.1875rem 0.5rem;
    border-radius: 1.25rem;
  `}
`;

export const ShowAllLink = styled(Link)`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.brand.primary.default};
    font-weight: 600;
  `}
`;

export const CarouselWrapper = styled.div`
  position: relative;
`;

export const ScrollContainer = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0.5rem 0 1.5rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

type ArrowProps = TransientProps<{ direction: 'left' | 'right' }>;

export const Arrow = styled.button<ArrowProps>`
  ${({ theme, $direction }) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${$direction === 'left' ? 'left: -0.5rem' : 'right: -0.5rem'};
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    color: ${theme.colors.foreground[100]};

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}
`;

export const RoomCard = styled.div`
  ${({ theme }) => css`
    width: calc((100% - 2rem) / 3);
    min-width: 13.75rem;
    background: ${theme.colors.background[100]};
    border-radius: 0.875rem;
    overflow: hidden;
    border: 1px solid ${theme.colors.background[40]};
    flex-shrink: 0;
    scroll-snap-align: start;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }

    > a {
      text-decoration: none;
      color: inherit;
    }

    ${down('md')} {
      min-width: 16rem;
    }
  `}
`;

export const RoomThumb = styled.div`
  ${({ theme }) => css`
    height: 10.3125rem;
    background: linear-gradient(
      135deg,
      ${theme.colors.background[60]},
      ${theme.colors.background[40]}
    );
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: absolute;
      inset: 0;
    }
  `}
`;

type StatusBadgeProps = TransientProps<{
  variant: 'live' | 'soon' | 'reprise';
}>;

const statusTheme = {
  live: { color: '#dc2626', bg: 'rgba(220, 38, 38, 0.12)' },
  soon: { color: '#e5a00d', bg: 'rgba(229, 160, 13, 0.12)' },
  reprise: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.12)' },
};

export const StatusBadge = styled.div<StatusBadgeProps>`
  ${({ $variant }) => css`
    align-self: flex-start;
    padding: 0.25rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    background: ${statusTheme[$variant].bg};
    color: ${statusTheme[$variant].color};
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  `}
`;

const pulseAnim = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

export const PulseDot = styled.span`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: #dc2626;
  animation: ${pulseAnim} 2s infinite;
`;

export const ViewersCount = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 1.25rem;
  font-size: 0.625rem;
  background: rgba(0, 0, 0, 0.35);
  color: white;
  display: flex;
  align-items: center;
  gap: 0.1875rem;
  z-index: 1;
`;

export const RoomInfo = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
`;

export const DayBadge = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.25rem 0.5rem;
    border-radius: 0.5rem;
    background: ${theme.colors.background[60]};
    color: ${theme.colors.foreground[80]};
    flex-shrink: 0;
    min-width: 2.5rem;
    text-transform: uppercase;

    > span:first-child {
      font-size: 1rem;
      font-weight: 700;
      line-height: 1.1;
    }

    > span:last-child {
      font-size: 0.5625rem;
      font-weight: 600;
      opacity: 0.7;
    }
  `}
`;

export const RoomDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;

  > h3 {
    font-size: 0.8512rem;
    font-weight: 600;
  }
`;
